import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import 'tippy.js/dist/tippy.css';

import Input from 'antd/es/input/Input';
import { memo, useState, useEffect } from 'react';
import { MdChevronRight } from 'react-icons/md';
import { useAtom } from 'jotai';
import styled from 'styled-components';
import { userInformationAtom } from '~/stores/UserStore';
import { currentProjectAtom } from '~/stores/ProjectStore';
import Menu from './components/Menu';
import { Button, Tooltip, message } from 'antd';
import Avatar from './components/Avatar';
import NotificationBell from './components/notification/NotificationBell';
import SwitcherLng from '~/components/common/SwitcherLng';
import { selectedMapDataAtom, showCommentPanelAtom, showSearchAddressPanelAtom, showSharePanelAtom } from '~/components/Map/stores/mapStore';
import { remote_mapData_updateStyle } from '~/common/typescript/apis/MapDataAPIs';
import { remote_updateProject } from '~/common/typescript/apis/ProjectAPIs';
import Routes from '~/routes/routes';
import { MAP_DATA_SOURCE_ID_PREFIX } from '~/components/Map/utils/MapDataUtils';
import BasemapSelector from './components/BasemapSelector/BasemapSelector';
import HeaderCenter from './HeaderCenter/HeaderCenter';
import MapView from './components/MapView/MapView';
import ElementHeaderController from './HeaderCenter/Element/ElementHeaderController';
import HeaderRight from './HeaderRight/HeaderRight';
import { UI_HeaderStyles } from './HeaderStyles';
import { PiFloppyDiskBold, PiCubeBold } from 'react-icons/pi';

function Header() {
  const { t } = useTranslation();

  const [userInformation] = useAtom(userInformationAtom);
  const [selectedMapData, setSelectedMapData] = useAtom(selectedMapDataAtom);
  const [currentProject, setCurrentProject] = useAtom(currentProjectAtom);
  const [projectName, setProjectName] = useState<string>(currentProject?.name || '');
  const [isSavingProjectName, setIsSavingProjectName] = useState<boolean>(false);

  useEffect(() => {
    setProjectName(currentProject?.name || '');
  }, [currentProject?.name]);

  const handleSaveProjectName = async () => {
    const trimmed = projectName.trim();
    if (!trimmed) {
      setProjectName(currentProject?.name || '');
      return;
    }
    if (!currentProject || !currentProject.id || trimmed === currentProject.name) {
      return;
    }

    try {
      setIsSavingProjectName(true);
      const res = await remote_updateProject({ name: trimmed }, currentProject.id);
      if (res && res.data) {
        setCurrentProject(res.data);
        message.success('Đã cập nhật tên dự án');
      } else {
        setProjectName(currentProject.name || '');
        message.error('Không thể cập nhật tên dự án');
      }
    } catch (err) {
      console.error('Failed to update project name:', err);
      setProjectName(currentProject.name || '');
      message.error('Lỗi khi cập nhật tên dự án');
    } finally {
      setIsSavingProjectName(false);
    }
  };

  const handleSave = () => {
    if (!selectedMapData || !selectedMapData.id) return;
    const sourceId = MAP_DATA_SOURCE_ID_PREFIX + selectedMapData.id;
    let style: any = {
      layers: []
    };
    window.map?.getStyle()?.layers.forEach((layer: any) => {
      if (layer.source === sourceId) {
        style.layers.push(layer);
      }
    })

    console.log('style', style);
    remote_mapData_updateStyle(selectedMapData.id, {
      content: JSON.stringify(style)
    });
  }


  return (
    <UI_HeaderContainer>
      {/* Left */}
      <UI_HeaderLeft>
        <Menu style={{ height: '100%' }} />
        <Link to={Routes.PROJECT}>
          <UI_GoToProjects>
            {t('project')}
          </UI_GoToProjects>
        </Link>
        <UI_ChevronIcon>
          <MdChevronRight />
        </UI_ChevronIcon>
        <UI_ProjectNameInput
          placeholder={t('project_name')}
          value={projectName}
          disabled={isSavingProjectName}
          onChange={(e) => setProjectName(e.target.value)}
          onBlur={handleSaveProjectName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              (e.target as HTMLInputElement).blur();
            } else if (e.key === 'Escape') {
              setProjectName(currentProject?.name || '');
              (e.target as HTMLInputElement).blur();
            }
          }}
          style={{ width: `${Math.max(120, Math.min(320, (projectName || '').length * 8.5 + 24))}px` }}
          className="project-name-input"
          title="Bấm để chỉnh sửa tên dự án"
        />
      </UI_HeaderLeft>
      {/* End Left */}

      {/* Center */}
      <UI_Center>
        <HeaderCenter />
      </UI_Center>
      {/* Center */}

      {/* Right */}
      <UI_Right_Wrapper>
        <UI_HeaderStyles.UI_Item>
          <Tooltip title='Hiển thị'>
            <MapView />
          </Tooltip>
        </UI_HeaderStyles.UI_Item>
        <UI_HeaderStyles.UI_Item>
          <Tooltip title='Bản đồ nền'>
            <BasemapSelector />
          </Tooltip>
        </UI_HeaderStyles.UI_Item>
        {userInformation && (
          <UI_HeaderStyles.UI_Item>
            <NotificationBell />
          </UI_HeaderStyles.UI_Item>
        )}
        <UI_HeaderStyles.UI_Item>
          <SwitcherLng theme="light" />
        </UI_HeaderStyles.UI_Item>
        <UI_HeaderStyles.UI_Item>
          {userInformation ? (
            <Tooltip title='Tài khoản'>
              <Avatar />
            </Tooltip>
          ) : (
            <Avatar />
          )}
        </UI_HeaderStyles.UI_Item>

        {/* <UI_HeaderStyles.UI_Item>
          <Tooltip title='Chế độ 3D'>
            <Link to={Routes.PROJECT_3D}>
              <UI_ModernButton>
                <PiCubeBold style={{ fontSize: '16px' }} />
                <span>3D</span>
              </UI_ModernButton>
            </Link>
          </Tooltip>
        </UI_HeaderStyles.UI_Item> */}

        <UI_Divider />

        <HeaderRight />

        {/* Lưu */}
        {selectedMapData && (
          <UI_HeaderStyles.UI_Item>
            <UI_SaveButton onClick={handleSave}>
              <PiFloppyDiskBold style={{ fontSize: '15px' }} />
              <span>Lưu</span>
            </UI_SaveButton>
          </UI_HeaderStyles.UI_Item>
        )}
        {/* End Lưu */}
      </UI_Right_Wrapper>
      {/* End Right */}

      <ElementHeaderController />
    </UI_HeaderContainer>
  );
}

export default memo(Header);

const UI_HeaderContainer = styled.header`
  width: 100%;
  height: var(--header-height);
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  backdrop-filter: blur(10px);
  z-index: 10;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.05),
    0 4px 12px rgba(59, 130, 246, 0.08);
  border-bottom: 1px solid rgba(59, 130, 246, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  box-sizing: border-box;

  @media (max-width: 768px) {
    height: 48px;
    flex-wrap: nowrap;
    overflow: hidden;
  }

  &:hover {
    box-shadow:
      0 2px 4px rgba(0, 0, 0, 0.06),
      0 6px 16px rgba(59, 130, 246, 0.12);
  }
`;

const UI_HeaderLeft = styled.ul`
  display: flex;
  align-items: center;
  height: 100%;
  justify-content: flex-start;
  flex: 1 1 0%;
  z-index: 2;
  gap: 8px;
  padding-left: 16px;
  margin: 0;
  list-style: none;

  @media (max-width: 768px) {
    flex: 0 0 auto;
    min-width: 0;
    gap: 4px;
    padding-left: 8px !important;
  }

  @media (max-width: 480px) {
    gap: 2px;
    padding-left: 4px !important;
  }
`;

const UI_ProjectNameInput = styled(Input)`
  height: 32px;
  min-width: 120px;
  max-width: 340px;
  padding: 4px 8px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    max-width: 160px;
    min-width: 90px;
    height: 28px;
    font-size: 12px;
    padding: 2px 6px;
  }

  @media (max-width: 480px) {
    display: none;
  }

  &:hover {
    border-color: #cbd5e1;
    background: rgba(0, 0, 0, 0.04);
    color: #0f172a;
  }

  &:focus,
  &:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
    outline: none;
    background: #ffffff;
    cursor: text;
    color: #0f172a;
  }

  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }
`;

const UI_GoToProjects = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 600;
  white-space: nowrap;
  min-width: fit-content;
  padding: 6px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  letter-spacing: 0.3px;

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 4px 8px;
  }

  @media (max-width: 480px) {
    display: none;
  }

  &:hover {
    color: #3b82f6;
    background: rgba(59, 130, 246, 0.08);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const UI_Divider = styled.div`
  height: 24px;
  width: 2px;
  background: linear-gradient(180deg, transparent 0%, #cbd5e1 50%, transparent 100%);
  margin: 0 12px;
  border-radius: 1px;

  @media (max-width: 768px) {
    height: 20px;
    margin: 0 6px;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const UI_Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 0 16px;

  @media (max-width: 768px) {
    padding: 0 8px;
    min-width: 0;
  }

  @media (max-width: 480px) {
    display: none;
  }
`

const UI_Right_Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
  padding-right: 16px;
  gap: 6px;

  @media (max-width: 768px) {
    flex: 0 0 auto;
    padding-right: 8px;
    gap: 4px;
  }

  @media (max-width: 480px) {
    padding-right: 4px;
    gap: 2px;
  }
`;

const UI_ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
  font-size: 20px;
  margin: 0 4px;
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    font-size: 16px;
    margin: 0 2px;
  }

  @media (max-width: 480px) {
    display: none;
  }

  svg {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.05));
  }
`;

const UI_ModernButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow:
    0 2px 4px rgba(59, 130, 246, 0.3),
    0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    box-shadow:
      0 4px 8px rgba(59, 130, 246, 0.4),
      0 2px 4px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    box-shadow:
      0 2px 4px rgba(59, 130, 246, 0.3),
      0 1px 2px rgba(0, 0, 0, 0.05);
  }
`;

const UI_SaveButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow:
    0 2px 4px rgba(16, 185, 129, 0.3),
    0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    box-shadow:
      0 4px 8px rgba(16, 185, 129, 0.4),
      0 2px 4px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    box-shadow:
      0 2px 4px rgba(16, 185, 129, 0.3),
      0 1px 2px rgba(0, 0, 0, 0.05);
  }
`;
